import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { publicGet } from "../lib/apiPublic";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Optional: reuse your media normalizer
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, "");
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
}

export default function MiniArtistCard({ id, onViewProfile, autoMs = 3000 }) {
  const [artist, setArtist] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [artworks, setArtworks] = React.useState([]);
  const [hover, setHover] = React.useState(false);
  const [idx, setIdx] = React.useState(0);

  // Fetch artworks (with images)
  React.useEffect(() => {
    let cancelled = false;
    publicGet(`/artworks/?artist=${id}&view=card`)
      .then((res) => {
        if (!cancelled) setArtworks(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setArtworks([]);
      });
    return () => { cancelled = true; };
  }, [id]);

  // Fetch artist profile
  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await publicGet(`/artists/${id}/`, { params: { view: "card" } });
        if (!cancel) setArtist(data);
        // console.log("MiniArtistCard data", data);
      } catch (e) {
        if (!cancel) setArtist(null);
        console.warn("MiniArtistCard fetch failed", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  // Flatten all artwork images into a single array
  const imageUrls = React.useMemo(() => {
    const urls = [];
    for (const a of artworks || []) {
      const imgs = Array.isArray(a?.images) ? a.images : [];
      for (const im of imgs) {
        const src = im?.url || im?.image || im?.image_url;
        if (src) urls.push(toRelativeMedia(src));
      }
      if (imgs.length === 0 && a?.primary_image_url) {
        urls.push(toRelativeMedia(a.primary_image_url));
      }
    }
    // Remove obvious duplicates while preserving order
    const seen = new Set();
    return urls.filter((u) => (seen.has(u) ? false : (seen.add(u), true)));
  }, [artworks]);

  // Clamp current index if images change
  React.useEffect(() => {
    if (imageUrls.length === 0) {
      setIdx(0);
      return;
    }
    setIdx((prev) => (prev >= imageUrls.length ? 0 : prev));
  }, [imageUrls]);

  // Auto-advance
  React.useEffect(() => {
    if (!imageUrls.length || imageUrls.length === 1) return;
    if (hover) return; // pause on hover
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % imageUrls.length);
    }, autoMs);
    return () => clearInterval(t);
  }, [imageUrls, hover, autoMs]);

  const goPrev = React.useCallback(() => {
    if (!imageUrls.length) return;
    setIdx((i) => (i - 1 + imageUrls.length) % imageUrls.length);
  }, [imageUrls]);

  const goNext = React.useCallback(() => {
    if (!imageUrls.length) return;
    setIdx((i) => (i + 1) % imageUrls.length);
  }, [imageUrls]);

  if (loading) {
    return (
      <Box sx={{ width: 240, p: 1, border: "1px solid #e5e7eb", borderRadius: 1, bgcolor: "#fff" }}>
        <Typography variant="body2" color="text.secondary">Loading artist…</Typography>
      </Box>
    );
  }

  if (!artist) {
    return (
      <Box sx={{ width: 240, p: 1, border: "1px solid #e5e7eb", borderRadius: 1, bgcolor: "#fff" }}>
        <Typography variant="body2" color="error.main">Failed to load artist.</Typography>
      </Box>
    );
  }

  const name =
    artist?.display_name ||
    `${artist?.user?.first_name || ""} ${artist?.user?.last_name || ""}`.trim() ||
    "Artist";

  return (
    <Box sx={{ width: 240, p: 1, borderRadius: 1.5, backgroundColor: "rgba(95, 90, 90, 0.26)" }}>
      <Typography variant="body2" color="text.primary" sx={{ mb: 0 }} >
        <strong>{name}</strong>
      </Typography>

      {/* Slider area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 120,
          borderRadius: 1.5,
          overflow: "hidden",
          userSelect: "none",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {imageUrls.length > 0 ? (
          <>
            <img
              key={imageUrls[idx]} // ensures quick cross-fade if you add transitions later
              src={imageUrls[idx]}
              alt={name}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer"
            />

            {/* Left / Right controls (visible on hover if >1 image) */}
            {imageUrls.length > 1 && (
              <>
                <IconButton
                  aria-label="Previous"
                  onClick={goPrev}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.9)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    display: hover ? "flex" : "none",
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Next"
                  onClick={goNext}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.9)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    display: hover ? "flex" : "none",
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </>
            )}

            {/* Dot indicators */}
            {imageUrls.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 6,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 0.75,
                }}
              >
                {imageUrls.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => setIdx(i)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: i === idx ? "common.white" : "rgba(255,255,255,0.6)",
                      border: "1px solid rgba(0,0,0,0.15)",
                      cursor: "pointer",
                    }}
                    title={`Image ${i + 1}`}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ width: "100%", height: "100%", bgcolor: "#f3f4f6" }} />
        )}
      </Box>

      {artist.artist_type && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
          <strong>Artist Type:</strong> {artist.artist_type}
        </Typography>
      )}

      {onViewProfile && (
        <Box sx={{ mt: 1 }}>
          <button
            onClick={onViewProfile}
            style={{
              textSizeAdjust: 'auto',
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #faf8f8ff",
              background: "rgba(255, 255, 255, 0.6)",
              cursor: "pointer",
            }}
          >
            View Profile
          </button>
        </Box>
      )} 
    </Box>
  );
}
